import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import VM from '@scratch/scratch-vm';
import {connect} from 'react-redux';

import ControlsComponent from '../components/controls/controls.jsx';
import log from '../lib/log.js';
import {
    getAvailableBurnCenterDevices,
    resolveBurnCenterEntry
} from '../lib/burn-center-devices.js';
import {compileMicroBitSource} from '../lib/microbit-makecode-compiler.js';
import {
    flashMicroBit,
    isMicroBitUpdateSupported,
    selectMicroBitDevice
} from '../lib/microbit-update.js';

const BURN_PROGRESS_WRITING = 65;
const BURN_PROGRESS_WAITING = 82;
const OPEN_BURN_CENTER_EVENT = 'OPEN_BURN_CENTER';
const CAR_CONNECTION_CHANGED_EVENT = 'DOMI_CAR_CONNECTION_CHANGED';
const CONTROLLER_HAT_OPCODE = 'domiController_whenButtonAction';
const CONTROLLER_BUTTONS = new Set(['UP', 'DOWN', 'LEFT', 'RIGHT', 'OK', 'A', 'B', 'C', 'D', 'E', 'F']);
const CONTROLLER_EVENT_HISTORY_LIMIT = 6;

class Controls extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleBurnCenterClose',
            'handleBurnCenterCompile',
            'handleBurnCenterDeviceSelect',
            'handleBurnCenterOpen',
            'handleBurnCenterRequest',
            'handleBurnCenterStart',
            'handleControllerButtonPress',
            'handleControllerButtonRelease',
            'handleControllerConnectionChanged',
            'handleControllerEmergencyStop',
            'handleControllerExtensionAdded',
            'handleControllerSimulatorClose',
            'handleControllerSimulatorOpen',
            'handleControllerVisibilityChange',
            'handleGreenFlagClick',
            'handleStopAllClick',
            'handleWindowBlur'
        ]);
        const extensionManager = props.vm.extensionManager;
        this.state = {
            burnCenterAvailableDevices: [],
            burnCenterConnected: false,
            burnCenterDevice: null,
            burnCenterInstructionCount: 0,
            burnCenterInstructions: [],
            burnCenterMessage: '',
            burnCenterOpen: false,
            burnCenterPhase: 'preparing',
            burnCenterProgress: 0,
            burnCenterSelectorOpen: false,
            burnCenterWebUsbSupported: false,
            controllerActiveButtons: [],
            controllerConnected: this.isCarConnected(),
            controllerEventHistory: [],
            controllerSimulatorAvailable: Boolean(
                extensionManager && extensionManager.isExtensionLoaded('domiController')
            ),
            controllerSimulatorOpen: false
        };
        this.burnProgressTimer = null;
        this.controllerEventId = 0;
        this.controllerPressedButtons = new Set();
        this.microBitHex = null;
    }
    componentDidMount () {
        this._isMounted = true;
        this.props.vm.on(OPEN_BURN_CENTER_EVENT, this.handleBurnCenterRequest);
        this.props.vm.on('EXTENSION_ADDED', this.handleControllerExtensionAdded);
        this.props.vm.runtime.on(CAR_CONNECTION_CHANGED_EVENT, this.handleControllerConnectionChanged);
        window.addEventListener('blur', this.handleWindowBlur);
        document.addEventListener('visibilitychange', this.handleControllerVisibilityChange);
    }
    componentWillUnmount () {
        clearTimeout(this.burnProgressTimer);
        this.releaseAllControllerButtons(true, false);
        this._isMounted = false;
        this.props.vm.removeListener(OPEN_BURN_CENTER_EVENT, this.handleBurnCenterRequest);
        this.props.vm.removeListener('EXTENSION_ADDED', this.handleControllerExtensionAdded);
        this.props.vm.runtime.removeListener(CAR_CONNECTION_CHANGED_EVENT, this.handleControllerConnectionChanged);
        window.removeEventListener('blur', this.handleWindowBlur);
        document.removeEventListener('visibilitychange', this.handleControllerVisibilityChange);
    }
    handleGreenFlagClick (e) {
        e.preventDefault();
        if (e.shiftKey) {
            this.props.vm.setTurboMode(!this.props.turbo);
        } else {
            if (!this.props.isStarted) {
                this.props.vm.start();
            }
            this.props.vm.greenFlag();
        }
    }
    handleStopAllClick (e) {
        e.preventDefault();
        this.props.vm.stopAll();
    }
    isCarConnected () {
        const isConnected = this.props.vm.runtime.getOpcodeFunction('domiCar_isConnected');
        return isConnected ? Boolean(isConnected({}, null)) : false;
    }
    handleControllerExtensionAdded (categoryInfo) {
        const extensionManager = this.props.vm.extensionManager;
        this.setState({
            controllerSimulatorAvailable: Boolean(
                (categoryInfo && categoryInfo.id === 'domiController') ||
                (extensionManager && extensionManager.isExtensionLoaded('domiController'))
            )
        });
    }
    handleControllerConnectionChanged ({connected} = {}) {
        if (!this._isMounted) return;
        this.setState({controllerConnected: Boolean(connected)});
    }
    handleControllerSimulatorOpen (event) {
        event.preventDefault();
        this.setState({
            controllerConnected: this.isCarConnected(),
            controllerSimulatorOpen: true
        });
    }
    handleControllerSimulatorClose () {
        this.releaseAllControllerButtons(true);
        this.setState({controllerSimulatorOpen: false});
    }
    handleControllerButtonPress (button) {
        if (!CONTROLLER_BUTTONS.has(button) || this.controllerPressedButtons.has(button)) return;
        if (!this.props.isStarted) this.props.vm.start();

        this.controllerPressedButtons.add(button);
        this.triggerControllerEvent(button, 'PRESS');
        this.recordControllerEvent(button, 'PRESS');
        this.setState({controllerActiveButtons: Array.from(this.controllerPressedButtons)});
    }
    handleControllerButtonRelease (button) {
        if (!this.controllerPressedButtons.has(button)) return;

        this.controllerPressedButtons.delete(button);
        this.triggerControllerEvent(button, 'RELEASE');
        this.recordControllerEvent(button, 'RELEASE');
        this.setState({controllerActiveButtons: Array.from(this.controllerPressedButtons)});
    }
    handleControllerEmergencyStop () {
        this.releaseAllControllerButtons(true);
    }
    handleWindowBlur () {
        if (this.state.controllerSimulatorOpen) this.releaseAllControllerButtons(true);
    }
    handleControllerVisibilityChange () {
        if (this.state.controllerSimulatorOpen && document.hidden) this.releaseAllControllerButtons(true);
    }
    triggerControllerEvent (button, action) {
        return this.props.vm.runtime.startHats(CONTROLLER_HAT_OPCODE, {
            ACTION: action,
            BUTTON: button
        }) || [];
    }
    recordControllerEvent (button, action) {
        if (!this._isMounted) return;
        const event = {
            action,
            button,
            id: ++this.controllerEventId
        };
        this.setState(previousState => ({
            controllerEventHistory: [event, ...previousState.controllerEventHistory]
                .slice(0, CONTROLLER_EVENT_HISTORY_LIMIT)
        }));
    }
    releaseAllControllerButtons (stopCar, updateState = true) {
        const pressedButtons = Array.from(this.controllerPressedButtons);
        this.controllerPressedButtons.clear();
        pressedButtons.forEach(button => {
            this.triggerControllerEvent(button, 'RELEASE');
            this.recordControllerEvent(button, 'RELEASE');
        });
        if (updateState && this._isMounted) this.setState({controllerActiveButtons: []});
        if (!stopCar) return;

        const stop = this.props.vm.runtime.getOpcodeFunction('domiCar_stop');
        if (!stop) return;
        Promise.resolve(stop({}, null)).catch(error => {
            log.warn(`Domi controller simulator could not stop the car: ${error.message}`);
        });
    }
    handleBurnCenterOpen (e) {
        e.preventDefault();
        if (this.state.burnCenterPhase === 'uploading') return;

        const availableDevices = getAvailableBurnCenterDevices(this.props.vm.extensionManager);
        const entry = resolveBurnCenterEntry(availableDevices);
        this.setState({
            burnCenterAvailableDevices: availableDevices,
            burnCenterDevice: entry.device,
            burnCenterOpen: false,
            burnCenterSelectorOpen: entry.selectorOpen
        });
        if (entry.device) return this.prepareBurnCenter(entry.device);
    }
    handleBurnCenterRequest (device) {
        if (this.state.burnCenterPhase === 'uploading') return;
        const availableDevices = getAvailableBurnCenterDevices(this.props.vm.extensionManager);
        if (!availableDevices.includes(device)) return;
        this.setState({burnCenterAvailableDevices: availableDevices});
        return this.prepareBurnCenter(device);
    }
    handleBurnCenterCompile () {
        if (!this.state.burnCenterDevice) return;
        return this.prepareBurnCenter(this.state.burnCenterDevice);
    }
    handleBurnCenterDeviceSelect (e) {
        const device = e.currentTarget.value;
        this.setState({
            burnCenterDevice: device,
            burnCenterSelectorOpen: false
        });
        return this.prepareBurnCenter(device);
    }
    async prepareBurnCenter (device) {
        if (this.state.burnCenterPhase === 'uploading') return;

        clearTimeout(this.burnProgressTimer);
        this.microBitHex = null;
        this.setState({
            burnCenterConnected: false,
            burnCenterDevice: device,
            burnCenterInstructionCount: 0,
            burnCenterInstructions: [],
            burnCenterMessage: '',
            burnCenterOpen: true,
            burnCenterPhase: 'preparing',
            burnCenterProgress: 0,
            burnCenterSelectorOpen: false,
            burnCenterWebUsbSupported: false
        });

        try {
            if (device === 'microbit') {
                const previewProgram = this.props.vm.runtime.getOpcodeFunction('microbit_previewOfflineProgram');
                if (!previewProgram) throw new Error('micro:bit preview service is unavailable');
                const preview = previewProgram({}, null);
                await this.prepareMicroBitBurn(preview);
                return;
            }

            if (device !== 'domiCar') throw new Error('Unsupported burn center device');
            const previewProgram = this.props.vm.runtime.getOpcodeFunction('domiCar_previewOfflineProgram');
            const isConnected = this.props.vm.runtime.getOpcodeFunction('domiCar_isConnected');
            if (!previewProgram) throw new Error('Domi Car preview service is unavailable');

            const preview = previewProgram({}, null);
            const connected = isConnected ? Boolean(isConnected({}, null)) : false;
            this.setState({
                burnCenterConnected: connected,
                burnCenterInstructionCount: preview.instructionCount || 0,
                burnCenterInstructions: preview.instructions || [],
                burnCenterMessage: preview.success ? '' : preview.message,
                burnCenterPhase: preview.success ? 'ready' : 'invalid',
                burnCenterProgress: 0
            });
        } catch (error) {
            this.setState({
                burnCenterMessage: error.message,
                burnCenterPhase: 'invalid',
                burnCenterProgress: 0
            });
        }
    }
    async prepareMicroBitBurn (preview) {
        const webUsbSupported = isMicroBitUpdateSupported();
        this.setState({
            burnCenterConnected: false,
            burnCenterDevice: 'microbit',
            burnCenterInstructionCount: preview.instructionCount || 0,
            burnCenterInstructions: preview.instructions || [],
            burnCenterMessage: preview.success ? '' : preview.message,
            burnCenterPhase: preview.success ? 'preparing' : 'invalid',
            burnCenterProgress: 0,
            burnCenterWebUsbSupported: webUsbSupported
        });
        if (!preview.success) return;

        try {
            this.microBitHex = await compileMicroBitSource(preview.source);
            this.setState({
                burnCenterPhase: 'ready',
                burnCenterProgress: 0
            });
        } catch (error) {
            this.microBitHex = null;
            this.setState({
                burnCenterMessage: `micro:bit program compilation failed: ${error.message}`,
                burnCenterPhase: 'invalid',
                burnCenterProgress: 0
            });
        }
    }
    handleBurnCenterClose () {
        if (this.state.burnCenterPhase === 'uploading') return;
        this.setState({
            burnCenterOpen: false,
            burnCenterSelectorOpen: false
        });
    }
    async handleBurnCenterStart () {
        if (this.state.burnCenterDevice === 'microbit') {
            if (
                !this.microBitHex ||
                !this.state.burnCenterWebUsbSupported ||
                this.state.burnCenterPhase !== 'ready'
            ) return;

            const devicePromise = selectMicroBitDevice();
            this.setState({
                burnCenterMessage: '',
                burnCenterPhase: 'uploading',
                burnCenterProgress: 5
            });
            try {
                const device = await devicePromise;
                if (!device) throw new Error('No micro:bit was selected');
                this.setState({burnCenterProgress: 10});
                await flashMicroBit(device, this.microBitHex, progress => {
                    const normalized = Math.max(0, Math.min(1, progress));
                    this.setState({burnCenterProgress: 10 + Math.round(normalized * 90)});
                });
                this.setState({
                    burnCenterMessage: '',
                    burnCenterPhase: 'success',
                    burnCenterProgress: 100
                });
            } catch (error) {
                const cancelled = error && error.name === 'NotFoundError';
                this.setState({
                    burnCenterMessage: cancelled ?
                        'No micro:bit was selected. You can close and try again.' : error.message,
                    burnCenterPhase: 'error',
                    burnCenterProgress: 0
                });
            }
            return;
        }

        if (!this.state.burnCenterConnected || this.state.burnCenterPhase !== 'ready') return;

        this.setState({
            burnCenterMessage: '',
            burnCenterPhase: 'uploading',
            burnCenterProgress: BURN_PROGRESS_WRITING
        });

        try {
            const uploadProgram = this.props.vm.runtime.getOpcodeFunction('domiCar_writeOfflineProgram');
            const getUploadStatus = this.props.vm.runtime.getOpcodeFunction('domiCar_offlineProgramStatus');
            if (!uploadProgram) throw new Error('Domi Car upload service is unavailable');

            this.burnProgressTimer = setTimeout(() => {
                this.setState({burnCenterProgress: BURN_PROGRESS_WAITING});
            }, 250);
            const [success] = await Promise.all([
                uploadProgram({}, null),
                new Promise(resolve => setTimeout(resolve, 500))
            ]);
            clearTimeout(this.burnProgressTimer);
            const message = getUploadStatus ? getUploadStatus({}, null) : '';
            this.setState({
                burnCenterConnected: success,
                burnCenterMessage: message,
                burnCenterPhase: success ? 'success' : 'error',
                burnCenterProgress: success ? 100 : 0
            });
        } catch (error) {
            clearTimeout(this.burnProgressTimer);
            this.setState({
                burnCenterConnected: false,
                burnCenterMessage: error.message,
                burnCenterPhase: 'error',
                burnCenterProgress: 0
            });
        }
    }
    render () {
        const {
            vm: _vm,
            isStarted: _isStarted,
            projectRunning,
            turbo,
            isFullScreen,
            ...props
        } = this.props;
        return (
            <ControlsComponent
                {...props}
                active={projectRunning}
                burnCenterAvailableDevices={this.state.burnCenterAvailableDevices}
                burnCenterConnected={this.state.burnCenterConnected}
                burnCenterDevice={this.state.burnCenterDevice}
                burnCenterInstructionCount={this.state.burnCenterInstructionCount}
                burnCenterInstructions={this.state.burnCenterInstructions}
                burnCenterMessage={this.state.burnCenterMessage}
                burnCenterOpen={this.state.burnCenterOpen}
                burnCenterPhase={this.state.burnCenterPhase}
                burnCenterProgress={this.state.burnCenterProgress}
                burnCenterSelectorOpen={this.state.burnCenterSelectorOpen}
                burnCenterWebUsbSupported={this.state.burnCenterWebUsbSupported}
                controllerActiveButtons={this.state.controllerActiveButtons}
                controllerConnected={this.state.controllerConnected}
                controllerEventHistory={this.state.controllerEventHistory}
                controllerSimulatorAvailable={this.state.controllerSimulatorAvailable}
                controllerSimulatorOpen={this.state.controllerSimulatorOpen}
                turbo={turbo}
                onBurnCenterClose={this.handleBurnCenterClose}
                onBurnCenterCompile={this.handleBurnCenterCompile}
                onBurnCenterDeviceSelect={this.handleBurnCenterDeviceSelect}
                onBurnCenterOpen={this.handleBurnCenterOpen}
                onBurnCenterStart={this.handleBurnCenterStart}
                onControllerButtonPress={this.handleControllerButtonPress}
                onControllerButtonRelease={this.handleControllerButtonRelease}
                onControllerEmergencyStop={this.handleControllerEmergencyStop}
                onControllerSimulatorClose={this.handleControllerSimulatorClose}
                onControllerSimulatorOpen={this.handleControllerSimulatorOpen}
                onGreenFlagClick={this.handleGreenFlagClick}
                onStopAllClick={this.handleStopAllClick}
                isFullScreen={isFullScreen}
            />
        );
    }
}

Controls.propTypes = {
    isFullScreen: PropTypes.bool,
    isStarted: PropTypes.bool.isRequired,
    projectRunning: PropTypes.bool.isRequired,
    turbo: PropTypes.bool.isRequired,
    vm: PropTypes.instanceOf(VM)
};

const mapStateToProps = state => ({
    isStarted: state.scratchGui.vmStatus.running,
    projectRunning: state.scratchGui.vmStatus.running,
    turbo: state.scratchGui.vmStatus.turbo
});
// no-op function to prevent dispatch prop being passed to component
const mapDispatchToProps = () => ({});

export {Controls};

export default connect(mapStateToProps, mapDispatchToProps)(Controls);
